"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import { SearchFilters as Filters } from "@/types/dto/search-filters/search-filters";
import { SearchRange } from "@/types/dto/search-filters/range-filter";
import { getOutgoingApiFieldName, parseFragmentParameters } from "@/config/unified-search-config";
import useTourSearch from "./use-tour-search";



// Helper function to merge filter data, preserving previous options during loading
const mergeFilterData = (
  currentData: Filters[],
  previousData: Filters[],
  isLoading: boolean
): Filters[] => {

  if (!isLoading || previousData.length === 0) {
    return currentData;
  }

  // console.log("Merging filter data during loading...");

  // During loading, merge current data with previous data to preserve all options
  const mergedFilters: Filters[] = [];

  previousData.forEach(prevFilter => {
    const currentFilter = currentData.find(f => f.fieldName === prevFilter.fieldName);

    if (currentFilter) {
      // Merge options from both current and previous, preferring current counts
      const mergedOptions = [...prevFilter.options];

      currentFilter.options.forEach(currentOption => {
        const existingIndex = mergedOptions.findIndex(opt => opt.value === currentOption.value);
        if (existingIndex >= 0) {
          // Update existing option with current count
          mergedOptions[existingIndex] = currentOption;
        } else {
          // Add new option
          mergedOptions.push(currentOption);
        }
      });

      mergedFilters.push({
        ...currentFilter,
        options: mergedOptions
      });
    } else {
      // Keep previous filter if not in current data
      mergedFilters.push(prevFilter);
    }
  });

  // Add any completely new filters from current data
  currentData.forEach(currentFilter => {
    if (!mergedFilters.find(f => f.fieldName === currentFilter.fieldName)) {
      mergedFilters.push(currentFilter);
    }
  });

  // console.log("Merged filters result:", mergedFilters.length);
  return mergedFilters;
};

const useSearchManager = (initialSearchParams?: { [key: string]: string | string[] | undefined }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Track previous filter data to maintain during loading
  const [previousFiltersData, setPreviousFiltersData] = useState<Filters[]>([]);
  const [previousRangesData, setPreviousRangesData] = useState<SearchRange[]>([]);
  // Track current hash to detect fragment parameter changes
  const [currentHash, setCurrentHash] = useState<string>("");
  // Listen for hash changes to detect fragment parameter updates
  useEffect(() => {
    const updateHash = () => {
      if (typeof window !== "undefined") {
        const newHash = window.location.hash;
        setCurrentHash(newHash);
      }
    };

    // Set initial hash
    updateHash();

    // Listen for hash changes
    window.addEventListener("hashchange", updateHash);

    // Also listen for popstate in case Next.js routing doesn't trigger hashchange
    window.addEventListener("popstate", updateHash);

    return () => {
      window.removeEventListener("hashchange", updateHash);
      window.removeEventListener("popstate", updateHash);
    };
  }, []);

  // Additional effect to check for hash changes on every render
  // This handles cases where Next.js Link doesn't trigger standard events
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (typeof window !== "undefined") {
      const actualHash = window.location.hash;
      if (actualHash !== currentHash) {
        setCurrentHash(actualHash);
      }
    }

    // Note: Cannot add [currentHash] dependency as it would create infinite loop
    // This effect intentionally runs on every render to detect hash changes
  });

  // Derive search parameters directly from URL - single source of truth
  const currentSearchFilters = useMemo(() => {
    const filters: Partial<SearchParameters> = {};

    // Step 1: Parse fragment parameters (legacy URLs with #q/)
    // Force re-parsing when hash changes - currentHash triggers re-computation
    const fragmentParams = parseFragmentParameters();

    // Explicit check to ensure currentHash dependency is meaningful
    if (currentHash && typeof window !== "undefined" && window.location.hash !== currentHash) {
      // This condition ensures currentHash is used and triggers re-computation
    }

    Object.entries(fragmentParams).forEach(([key, value]) => {
      if (key !== "currentPage" && value !== undefined) {
        // Apply outgoing API parameter mappings
        const apiFieldName = getOutgoingApiFieldName(key);
        if ((apiFieldName === "months" || apiFieldName === "Continent" || apiFieldName === "Country" || apiFieldName === "Region" || apiFieldName === "City") && typeof value === "string") {
          (filters as any)[apiFieldName] = value.split(",").filter(Boolean);
        } else if (Array.isArray(value)) {
          (filters as any)[apiFieldName] = value;
        } else if (typeof value === "string" && value.includes(",")) {
          // Handle comma-separated values for multi-select fields
          (filters as any)[apiFieldName] = value.split(",").filter(Boolean);
        } else {
          (filters as any)[apiFieldName] = value;
        }
      }
    });

    // Step 2: Use initialSearchParams if provided (for SSR) - these override fragment params
    if (initialSearchParams) {
      Object.entries(initialSearchParams).forEach(([key, value]) => {
        if (key !== "currentPage" && value !== undefined) {
          // Handle keyword -> TourId mapping for numeric values (1-3 digits)
          let finalKey = key;
          if (key === "keyword") {
            const valueAsString = Array.isArray(value) ? value.join(",") : String(value);
            const numericValue = Number(valueAsString);
            if (Number.isInteger(numericValue) && numericValue > 0 && numericValue <= 999) {
              finalKey = "TourId";
            }
          }
          // Apply outgoing API parameter mappings
          const apiFieldName = getOutgoingApiFieldName(finalKey);
          if ((apiFieldName === "months" || apiFieldName === "Continent" || apiFieldName === "Country" || apiFieldName === "Region" || apiFieldName === "City") && typeof value === "string") {
            (filters as any)[apiFieldName] = value.split(",").filter(Boolean);
          } else if (typeof value === "string" && value.includes(",")) {
            // Handle comma-separated values for multi-select fields
            (filters as any)[apiFieldName] = value.split(",").filter(Boolean);
          } else {
            (filters as any)[apiFieldName] = value;
          }
        }
      });
    }

    // Step 3: Overlay with current searchParams (for client-side navigation) - highest priority
    if (searchParams) {
      searchParams.forEach((value, key) => {
        if (key !== "currentPage") {
          // Handle keyword -> TourId mapping for numeric values (1-3 digits)
          let finalKey = key;
          if (key === "keyword") {
            const numericValue = Number(value);
            if (Number.isInteger(numericValue) && numericValue > 0 && numericValue <= 999) {
              finalKey = "TourId";
            }
          }

          // Apply outgoing API parameter mappings
          const apiFieldName = getOutgoingApiFieldName(finalKey);
          if ((apiFieldName === "months" || apiFieldName === "Continent" || apiFieldName === "Country" || apiFieldName === "Region" || apiFieldName === "City") && typeof value === "string") {
            (filters as any)[apiFieldName] = value.split(",").filter(Boolean);
          } else if (typeof value === "string" && value.includes(",")) {
            // Handle comma-separated values for multi-select fields
            (filters as any)[apiFieldName] = value.split(",").filter(Boolean);
          } else {
            (filters as any)[apiFieldName] = value;
          }
        }
      });
    }

    return filters;
  }, [searchParams, initialSearchParams, currentHash]);

  // Derive current page directly from URL
  const currentPage = useMemo(() => {
    // Check fragment parameters first - currentHash triggers re-computation
    const fragmentParams = parseFragmentParameters();
    const pageFromFragment = fragmentParams.currentPage;

    // Ensure currentHash dependency is meaningful
    if (currentHash && typeof window !== "undefined" && window.location.hash !== currentHash) {
      // This ensures currentHash is used in the computation
    }

    const pageFromUrl = searchParams ? searchParams.get("currentPage") : null;
    const pageFromInitial = initialSearchParams?.currentPage;

    if (pageFromUrl) {
      return parseInt(pageFromUrl, 10) || 1;
    }
    if (pageFromFragment && typeof pageFromFragment === "string") {
      return parseInt(pageFromFragment, 10) || 1;
    }
    if (pageFromInitial) {
      return parseInt(pageFromInitial as string, 10) || 1;
    }
    return 1;
  }, [searchParams, initialSearchParams, currentHash]);

  // Derive search source from URL parameters
  const interaction = useMemo(() => (searchParams ? searchParams.get("interaction") : null) || "unknown", [searchParams]);

  const handleFilterChange = useCallback((newFilters: Partial<SearchParameters>) => {
    const params = new URLSearchParams(searchParams ? searchParams.toString() : "");
    params.set("interaction", "filter_interaction");

    // Apply new filters to URL parameters
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        params.delete(key);
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.join(","));
        } else {
          params.delete(key);
        }
      } else {
        params.set(key, String(value));
      }
    });
    // Reset to page 1 when filters change
    params.set("currentPage", "1");
    // Navigate to new URL
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [searchParams, router, pathname]);



  const handleSortChange = (value: string) => {
    handleFilterChange({ sortBy: value });
  };

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
    const params = new URLSearchParams(searchParams ? searchParams.toString() : "");
    params.set("currentPage", value.toString());
    router.push(`${pathname}?${params.toString()}`, { scroll: true });
  }, [searchParams, router, pathname]);

  const handleClearAll = useCallback(() => {
    const hasSortBy = searchParams ? searchParams.has("sortBy") : false;
    if (hasSortBy) {
      const sortBySearchParam = searchParams ? searchParams.get("sortBy") || "" : "";
      // Navigate to the base path with only page 1, clearing all filters
      router.push(`${pathname}?currentPage=1&interaction=filter_interaction&sortBy=${sortBySearchParam}`, { scroll: false });
    } else {
      // Navigate to the base path with only page 1, clearing all filters
      router.push(`${pathname}?currentPage=1&interaction=filter_interaction`, { scroll: false });
    }
  }, [searchParams, router, pathname]);

  const handleFilterDelete = useCallback((filter: Record<string, string>) => {
    const [fieldName, valueToRemove] = Object.entries(filter)[0];
    const params = new URLSearchParams(searchParams ? searchParams.toString() : "");

    // Mapping of display names to actual URL parameter names (for fields that should be entirely deleted)
    const fieldNameMapping: Record<string, string | string[]> = {
      "Price Range": ["PriceFrom", "PriceTo"],
      "Duration": ["TourLengthFrom", "TourLengthTo"]
    };

    const urlFieldNames = fieldNameMapping[fieldName];

    if (urlFieldNames) {
      // Handle mapped fields (delete entire field(s))
      const fieldsToDelete = Array.isArray(urlFieldNames) ? urlFieldNames : [urlFieldNames];
      fieldsToDelete.forEach(field => params.delete(field));
    } else if (fieldName === "months" || fieldName === "Continent" || fieldName === "Country" || fieldName === "Region" || fieldName === "City" || fieldName === "TourStyles") {
      // Special handling for months, location fields, and TourStyles - they are stored as comma-separated values
      const currentValue = params.get(fieldName);
      if (currentValue) {
        const currentValues = currentValue.split(",").filter(Boolean);
        const updatedValues = currentValues.filter(v => v !== valueToRemove);

        params.delete(fieldName);
        if (updatedValues.length > 0) {
          params.set(fieldName, updatedValues.join(","));
        }
      }
    } else {
      // Handle other multi-select filters that use multiple URL parameters
      const currentValues = params.getAll(fieldName);
      const updatedValues = currentValues.filter(v => v !== valueToRemove);

      params.delete(fieldName);
      updatedValues.forEach(value => params.append(fieldName, value));
    }

    // Reset to page 1 when filters change
    params.set("currentPage", "1");
    // Set source to filter_interaction when user deletes filters
    params.set("interaction", "filter_interaction");
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [searchParams, router, pathname]);

  // Memoize search query parameters to prevent unnecessary re-fetches
  const searchQueryParams = useMemo(() => ({
    currentPage,
    pageSize: 12,
    ...currentSearchFilters
  }), [currentPage, currentSearchFilters]);

  // Fetch search data
  const searchQuery = useTourSearch(searchQueryParams);

  // Combined useEffect for handling search results
  useEffect(() => {
    if (!searchQuery.isLoading && searchQuery.data) {
      // Check if searching by TourId and only one result found - redirect immediately
      if (searchQueryParams.TourId &&
        searchQuery.data.searchSettings?.totalCount === 1 &&
        searchQuery.data.searchResults?.[0]?.tourUrl) {
        router.push(searchQuery.data.searchResults[0].tourUrl);
        return; // Exit early to prevent normal processing
      }

      // Normal processing for multiple results or non-TourId searches
      if (searchQuery.data.searchFilters && searchQuery.data.searchFilters.length > 0) {
        setPreviousFiltersData(searchQuery.data.searchFilters);
      }
      if (searchQuery.data.searchRanges && searchQuery.data.searchRanges.length > 0) {
        setPreviousRangesData(searchQuery.data.searchRanges);
      }
    }
  }, [searchQuery.isLoading, searchQuery.data, searchQueryParams.TourId, router]);

  const handleError = () => {
    searchQuery.refetch();
  };

  return {
    // Data
    data: searchQuery.data,
    isLoading: searchQuery.isLoading,
    isError: searchQuery.isError,

    // State
    currentPage,
    searchFilters: currentSearchFilters,
    searchSource: interaction,

    // Actions
    handleFilterChange,
    handlePageChange,
    handleClearAll,
    handleFilterDelete,
    handleSortChange,
    handleError,

    // Computed values for components
    filtersData: mergeFilterData(
      searchQuery.data?.searchFilters ?? [],
      previousFiltersData,
      searchQuery.isLoading
    ),
    rangesData: searchQuery.isLoading ? previousRangesData : (searchQuery.data?.searchRanges ?? []),
    searchResults: searchQuery.data?.searchResults ?? [],
    searchSettings: searchQuery.data?.searchSettings,
    sortOptions: searchQuery.data?.sortOptions ?? []
  };
};
export default useSearchManager;